// ============================================================
// Datei: frontend/src/pages/PnlReport.tsx
// P&L Report (Bybit-Style) — Total P&L, kumulierte Kurve, Symbol-Ranking.
// Datenquelle: gespeicherte Trades des aktiven Accounts (1 API-Call).
// ============================================================
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AreaChart, Area, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, LabelList,
} from 'recharts';
import { pnlApi, PnlOverview } from '../services/api';
import './PnlReport.css';

const GREEN = '#2ebd85';
const RED = '#f6465d';

type WindowTab = '7D' | '30D' | '60D' | '90D' | '180D' | 'custom';
const PRESET_DAYS: Record<Exclude<WindowTab, 'custom'>, number> = {
  '7D': 7, '30D': 30, '60D': 60, '90D': 90, '180D': 180,
};

const fmtUsd = (n: number) =>
  `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;

// YYYY-MM-DD aus einem Date (UTC)
const toDateInput = (d: Date) => d.toISOString().slice(0, 10);

const PnlReport: React.FC = () => {
  const [tab, setTab] = useState<WindowTab>('30D');
  // Custom-Range Defaults: letzte 30 Tage
  const [customFrom, setCustomFrom] = useState(() => {
    const d = new Date(); d.setUTCDate(d.getUTCDate() - 30); return toDateInput(d);
  });
  const [customTo, setCustomTo] = useState(() => toDateInput(new Date()));

  const [data, setData] = useState<PnlOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Aktuelles Zeitfenster als ISO-UTC ableiten
  const { fromISO, toISO } = useMemo(() => {
    if (tab === 'custom') {
      return {
        fromISO: `${customFrom}T00:00:00Z`,
        toISO: `${customTo}T23:59:59Z`,
      };
    }
    const to = new Date();
    const from = new Date(to);
    from.setUTCDate(from.getUTCDate() - PRESET_DAYS[tab]);
    return { fromISO: from.toISOString(), toISO: to.toISOString() };
  }, [tab, customFrom, customTo]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await pnlApi.overview(fromISO, toISO);   // EIN Call für alle Komponenten
      setData(res.data);
    } catch (e: any) {
      setError(e?.response?.data || e?.message || 'Fehler beim Laden des P&L-Reports.');
    } finally {
      setLoading(false);
    }
  }, [fromISO, toISO]);

  useEffect(() => { load(); }, [load]);

  // Grün/Rot um die Null-Linie: Gradient-Offset am Nulldurchgang
  const gradientOffset = useMemo(() => {
    if (!data?.curve.length) return 1;
    const vals = data.curve.map(p => p.cumulative_pnl);
    const max = Math.max(...vals, 0);
    const min = Math.min(...vals, 0);
    if (max <= 0) return 0;       // alles negativ → komplett rot
    if (min >= 0) return 1;       // alles positiv → komplett grün
    return max / (max - min);
  }, [data]);

  const dataAsOf = useMemo(() => {
    if (!data) return '';
    const d = new Date(data.data_as_of);
    return isNaN(d.getTime()) ? data.data_as_of
      : `${d.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
  }, [data]);

  const hasTrades = !!data && data.curve.length > 0 && data.ranking.length > 0;
  // Höhe des Ranking-Charts an Symbolanzahl koppeln
  const rankingHeight = Math.max(160, (data?.ranking.length ?? 0) * 38 + 20);

  return (
    <div className="pnl-report">
      {/* Header */}
      <div className="pnl-header">
        <div>
          <h1>P&amp;L Report</h1>
          <span className="pnl-subtitle">Realisierter P&amp;L des aktiven Accounts</span>
        </div>
        {data && <div className="pnl-asof">Data as of {dataAsOf}</div>}
      </div>

      {/* Zeitfenster-Tabs */}
      <div className="pnl-tabs">
        {(['7D', '30D', '60D', '90D', '180D', 'custom'] as WindowTab[]).map(t => (
          <button
            key={t}
            className={`pnl-tab${tab === t ? ' pnl-tab-active' : ''}`}
            onClick={() => setTab(t)}
          >
            {t === 'custom' ? 'Custom' : t}
          </button>
        ))}
        {tab === 'custom' && (
          <div className="pnl-daterange">
            <input
              type="date" value={customFrom} max={customTo}
              onChange={e => setCustomFrom(e.target.value)}
            />
            <span>→</span>
            <input
              type="date" value={customTo} min={customFrom} max={toDateInput(new Date())}
              onChange={e => setCustomTo(e.target.value)}
            />
          </div>
        )}
      </div>

      {/* Kennzahlen-Header */}
      <div className="pnl-kpis">
        <div className="metric-card pnl-kpi">
          <h3>Total P&amp;L</h3>
          <div
            className="metric-value"
            style={{ color: data && data.total_pnl < 0 ? RED : data ? GREEN : undefined }}
          >
            {data ? fmtUsd(data.total_pnl) : '—'}
          </div>
          <div className="metric-sub">Realisiert im gewählten Zeitfenster</div>
        </div>
        <div className="metric-card pnl-kpi">
          <h3>Trading Volume</h3>
          {data && !data.volume_available ? (
            <>
              <div className="metric-value pnl-muted">n/a</div>
              <div className="metric-sub">Keine Volumendaten (entry_price / contracts) hinterlegt</div>
            </>
          ) : (
            <>
              <div className="metric-value">{data ? fmtUsd(data.trading_volume) : '—'}</div>
              <div className="metric-sub">Notional = Menge × Einstiegspreis</div>
            </>
          )}
        </div>
      </div>

      {/* States */}
      {loading && <div className="pnl-state">⏳ Lade P&amp;L-Daten…</div>}

      {!loading && error && (
        <div className="pnl-state pnl-error">
          ⚠️ {String(error)}
          <button className="pnl-retry" onClick={load}>Erneut versuchen</button>
        </div>
      )}

      {!loading && !error && !hasTrades && (
        <div className="pnl-state">
          📭 Keine Trades im gewählten Zeitraum.
        </div>
      )}

      {/* Chart + Ranking (zweispaltig, mobil gestapelt) */}
      {!loading && !error && hasTrades && data && (
        <div className="pnl-grid">
          {/* P&L Chart (kumuliert) */}
          <div className="chart-container">
            <h2>Kumulierter P&amp;L</h2>
            <ResponsiveContainer width="100%" height={320}>
              <AreaChart data={data.curve} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="pnlFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset={gradientOffset} stopColor={GREEN} stopOpacity={0.5} />
                    <stop offset={gradientOffset} stopColor={RED} stopOpacity={0.5} />
                  </linearGradient>
                  <linearGradient id="pnlStroke" x1="0" y1="0" x2="0" y2="1">
                    <stop offset={gradientOffset} stopColor={GREEN} stopOpacity={1} />
                    <stop offset={gradientOffset} stopColor={RED} stopOpacity={1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e2539" />
                <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 11 }} minTickGap={24} />
                <YAxis
                  tick={{ fill: '#64748b', fontSize: 11 }}
                  tickFormatter={(v: number) => `$${v.toLocaleString('en-US')}`}
                  width={70}
                />
                <Tooltip
                  contentStyle={{ background: '#13172b', border: '1px solid #1e2539', borderRadius: 8 }}
                  labelStyle={{ color: '#e2e8f0' }}
                  formatter={(v: any) => [fmtUsd(Number(v)), 'Kumuliert']}
                />
                <Area
                  type="monotone"
                  dataKey="cumulative_pnl"
                  stroke="url(#pnlStroke)"
                  strokeWidth={2}
                  fill="url(#pnlFill)"
                  baseValue={0}
                  dot={false}
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* P&L Ranking pro Symbol */}
          <div className="chart-container">
            <h2>P&amp;L Ranking pro Symbol</h2>
            <ResponsiveContainer width="100%" height={rankingHeight}>
              <BarChart
                layout="vertical"
                data={data.ranking}
                margin={{ top: 4, right: 64, left: 8, bottom: 4 }}
              >
                <CartesianGrid horizontal={false} strokeDasharray="3 3" stroke="#1e2539" />
                <XAxis type="number" hide />
                <YAxis
                  type="category"
                  dataKey="symbol"
                  tick={{ fill: '#cbd5e1', fontSize: 12, fontWeight: 600 }}
                  width={56}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  cursor={{ fill: '#1e2539', opacity: 0.4 }}
                  contentStyle={{ background: '#13172b', border: '1px solid #1e2539', borderRadius: 8 }}
                  formatter={(v: any) => [fmtUsd(Number(v)), 'P&L']}
                />
                <Bar dataKey="pnl" radius={[4, 4, 4, 4]} isAnimationActive={false}>
                  {data.ranking.map((r, i) => (
                    <Cell key={i} fill={r.pnl >= 0 ? GREEN : RED} />
                  ))}
                  <LabelList
                    dataKey="pnl"
                    position="right"
                    formatter={(v: any) => fmtUsd(Number(v))}
                    style={{ fill: '#e2e8f0', fontSize: 11, fontWeight: 600 }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

export default PnlReport;
